import { useId, useMemo, useRef, useState } from "react";
import type { FormEvent } from "react";
import { ApiError, problemMessage } from "@/api/client";
import type {
  Animal,
  CreatePurchaseRequest,
  CreateSaleRequest,
  Guid,
  LivestockPostingResponse,
  PurchaseAnimalLine,
  SaleAnimalLine,
} from "@/api/types";
import { DialogShell } from "./DialogShell";
import "./LivestockBatchDialog.css";

/**
 * ef-041-6 — the "Buy"/"Sell" batch dialog Livestock.tsx opens from its own header action
 * (Buy) or a row's Sell action (behind `RowActions`'s "More actions" disclosure, same
 * placement as "Change status"). ONE component for both directions because the shapes
 * mirror each other line-for-line in `LivestockContracts.cs`: N lines, each its own
 * `pricePerHead`, one live running total, one POST that returns one ledger entry for the
 * WHOLE batch (`LivestockPostingResponse` — see that type's own remarks on the field it
 * does NOT carry).
 *
 * SELL LOCKS ITS FIRST LINE'S IDENTITY — opened from a specific row, the triggering animal
 * is always line 0 and its tag renders as plain text, never a picker (same "once-set,
 * never editable here" convention `AnimalDialog.tsx` uses for `tag` in edit mode), so a
 * farmer cannot accidentally sell a different animal than the one they clicked. Further
 * lines (via "Add animal") pick from any OTHER currently-active animal.
 *
 * PURCHASE LINES PICK OR CREATE — `ValidatePurchaseLines` (`LivestockEndpoints.cs`) accepts
 * exactly one of a fresh `tag` or an `existingAnimalId` per line, never both; the per-line
 * "Type" select drives which field this form actually sends.
 */

type PurchaseLineSource = "new" | "existing";

interface LineState {
  key: string;
  source: PurchaseLineSource;
  /** New-tag text (purchase, source "new") OR the locked display tag (sale line 0). */
  tag: string;
  /** The referenced animal id — purchase "existing" lines, and every sale line. */
  animalId: string;
  /** True only for a sale dialog's line 0, the row that opened it — never removable, its
   *  identity is never re-picked. */
  locked: boolean;
  pricePerHead: string;
}

function formatMoney(amount: number): string {
  return `$${amount.toFixed(2)}`;
}

export function LivestockBatchDialog({
  kind,
  entityId,
  animals,
  initialAnimal = null,
  onClose,
  onSubmitPurchase,
  onSubmitSale,
}: {
  kind: "purchase" | "sale";
  /** `useLivestock.ts`'s own `entityId` — required to post either batch. `null` blocks
   *  submit with an inline message rather than sending a guessed id. */
  entityId: Guid | null;
  /** The whole roster — feeds the "Existing animal" picker (purchase) and the "Animal"
   *  picker for any line beyond a sale's locked first one. */
  animals: Animal[];
  /** Sale only — the row that opened this dialog; ignored for `kind === "purchase"`. */
  initialAnimal?: Animal | null;
  /** Called on Cancel, Escape, an overlay click, AND after a successful post — same
   *  division `AnimalDialog.tsx` uses. */
  onClose: () => void;
  onSubmitPurchase: (body: CreatePurchaseRequest) => Promise<LivestockPostingResponse>;
  onSubmitSale: (body: CreateSaleRequest) => Promise<LivestockPostingResponse>;
}) {
  const idPrefix = useId();
  const lineCounter = useRef(0);

  function nextKey(): string {
    lineCounter.current += 1;
    return `line-${lineCounter.current}`;
  }

  function newPurchaseLine(): LineState {
    return { key: nextKey(), source: "new", tag: "", animalId: "", locked: false, pricePerHead: "" };
  }

  function newSaleLine(locked: boolean, animal?: Animal | null): LineState {
    return {
      key: nextKey(),
      source: "existing",
      tag: animal?.tag ?? "",
      animalId: animal?.id ?? "",
      locked,
      pricePerHead: "",
    };
  }

  // Lazy initializer only — `kind`/`initialAnimal` never change across this component's
  // mounted lifetime (Livestock.tsx mounts a fresh instance per open, same convention
  // `AnimalDialog` uses for `dialogOpen && <AnimalDialog .../>`).
  //
  // PURCHASE STARTS EMPTY — every line is something "Add animal" adds, never a pre-seeded
  // blank one; a farmer who adds N lines ends up with exactly N, not N+1. SALE STARTS WITH
  // EXACTLY the triggering row's own line, LOCKED — that one is never something the farmer
  // adds themselves.
  const [lines, setLines] = useState<LineState[]>(() =>
    kind === "purchase" ? [] : [newSaleLine(true, initialAnimal)],
  );
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const formId = useId();

  const total = useMemo(
    () =>
      lines.reduce((sum, line) => {
        const n = Number(line.pricePerHead);
        return sum + (Number.isFinite(n) ? n : 0);
      }, 0),
    [lines],
  );

  function addLine(): void {
    setLines((prev) => [...prev, kind === "purchase" ? newPurchaseLine() : newSaleLine(false)]);
  }

  function removeLine(key: string): void {
    setLines((prev) => prev.filter((line) => line.key !== key));
  }

  function updateLine(key: string, patch: Partial<LineState>): void {
    setLines((prev) => prev.map((line) => (line.key === key ? { ...line, ...patch } : line)));
  }

  /*
   * @ui-check: client-side ONLY — mirrors ValidatePurchaseLines/ValidateSaleLines
   * (LivestockEndpoints.cs) so a farmer sees the same reason immediately, without a round
   * trip. The server's own checks remain the real contract enforcement.
   */
  function clientValidate(): string | null {
    if (lines.length === 0) return "At least one animal is required.";
    for (const line of lines) {
      if (kind === "purchase") {
        if (line.source === "new") {
          if (line.tag.trim() === "") return "Tag is required.";
        } else if (!line.animalId) {
          return "Select an existing animal.";
        }
      } else if (!line.animalId) {
        return "Select an animal.";
      }
      const price = Number(line.pricePerHead);
      if (!(price > 0)) return "Each animal needs a price per head greater than 0.";
    }
    return null;
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setFormError(null);

    const problem = clientValidate();
    if (problem) {
      setFormError(problem);
      return;
    }
    if (entityId === null) {
      setFormError("No entity found for this tenant yet.");
      return;
    }

    setSaving(true);
    try {
      if (kind === "purchase") {
        const body: CreatePurchaseRequest = {
          entityId,
          animals: lines.map(
            (line): PurchaseAnimalLine =>
              line.source === "new"
                ? { tag: line.tag.trim(), pricePerHead: Number(line.pricePerHead) }
                : { existingAnimalId: line.animalId, pricePerHead: Number(line.pricePerHead) },
          ),
        };
        await onSubmitPurchase(body);
      } else {
        const body: CreateSaleRequest = {
          entityId,
          animals: lines.map(
            (line): SaleAnimalLine => ({ animalId: line.animalId, pricePerHead: Number(line.pricePerHead) }),
          ),
        };
        await onSubmitSale(body);
      }
      onClose();
    } catch (err) {
      // A 409 on a sale means one or more animals raced to non-active since the dialog
      // opened (LivestockEndpoints.cs's own AnimalNotActive outcome) — the whole batch was
      // refused, nothing posted.
      if (kind === "sale" && err instanceof ApiError && err.status === 409) {
        setFormError("One or more animals in this sale are no longer active. Nothing was posted.");
      } else {
        setFormError(
          problemMessage(err, kind === "purchase" ? "Could not post this purchase." : "Could not post this sale."),
        );
      }
    } finally {
      setSaving(false);
    }
  }

  function existingAnimalOptions(forLine: LineState) {
    return animals.filter(
      (a) => a.id === forLine.animalId || !lines.some((other) => other.key !== forLine.key && other.animalId === a.id),
    );
  }

  function activeAnimalOptions(forLine: LineState) {
    return animals.filter(
      (a) =>
        (a.status === "active" || a.id === forLine.animalId) &&
        (a.id === forLine.animalId || !lines.some((other) => other.key !== forLine.key && other.animalId === a.id)),
    );
  }

  return (
    <DialogShell
      title={kind === "purchase" ? "Buy animals" : "Sell animals"}
      size="lg"
      onClose={onClose}
      busy={saving}
      footer={
        <>
          <button type="button" className="btn btn--ghost" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="submit" form={formId} className="btn btn--primary" disabled={saving}>
            {saving ? "…" : kind === "purchase" ? "Buy" : "Sell"}
          </button>
        </>
      }
    >
      <form id={formId} className="livestock-batch-dialog__form" onSubmit={(e) => void handleSubmit(e)} noValidate>
        {lines.length === 0 && (
          <p className="livestock-batch-dialog__empty">No animals added yet. Add one below.</p>
        )}
        <ul className="livestock-batch-dialog__lines">
          {lines.map((line, index) => {
            const typeId = `${idPrefix}-type-${line.key}`;
            const tagId = `${idPrefix}-tag-${line.key}`;
            const animalId = `${idPrefix}-animal-${line.key}`;
            const priceId = `${idPrefix}-price-${line.key}`;

            return (
              <li key={line.key} className="livestock-batch-dialog__line">
                {kind === "purchase" ? (
                  <>
                    <div className="livestock-batch-dialog__field">
                      <label htmlFor={typeId}>Type</label>
                      <select
                        id={typeId}
                        value={line.source}
                        onChange={(e) =>
                          updateLine(line.key, {
                            source: e.target.value as PurchaseLineSource,
                            tag: "",
                            animalId: "",
                          })
                        }
                      >
                        <option value="new">New animal</option>
                        <option value="existing">Existing animal</option>
                      </select>
                    </div>
                    {line.source === "new" ? (
                      <div className="livestock-batch-dialog__field">
                        <label htmlFor={tagId}>Tag</label>
                        <input
                          id={tagId}
                          type="text"
                          maxLength={50}
                          value={line.tag}
                          onChange={(e) => updateLine(line.key, { tag: e.target.value })}
                        />
                      </div>
                    ) : (
                      <div className="livestock-batch-dialog__field">
                        <label htmlFor={animalId}>Existing animal</label>
                        <select
                          id={animalId}
                          value={line.animalId}
                          onChange={(e) => updateLine(line.key, { animalId: e.target.value })}
                        >
                          <option value="">Select…</option>
                          {existingAnimalOptions(line).map((a) => (
                            <option key={a.id} value={a.id}>
                              {a.tag}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                  </>
                ) : line.locked ? (
                  <p className="livestock-batch-dialog__tag">{line.tag}</p>
                ) : (
                  <div className="livestock-batch-dialog__field">
                    <label htmlFor={animalId}>Animal</label>
                    <select
                      id={animalId}
                      value={line.animalId}
                      onChange={(e) => {
                        const picked = animals.find((a) => a.id === e.target.value);
                        updateLine(line.key, { animalId: e.target.value, tag: picked?.tag ?? "" });
                      }}
                    >
                      <option value="">Select…</option>
                      {activeAnimalOptions(line).map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.tag}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="livestock-batch-dialog__field">
                  <label htmlFor={priceId}>Price per head</label>
                  <input
                    id={priceId}
                    type="number"
                    min="0.01"
                    step="0.01"
                    value={line.pricePerHead}
                    onChange={(e) => updateLine(line.key, { pricePerHead: e.target.value })}
                  />
                </div>

                {!line.locked && lines.length > 1 && (
                  <button
                    type="button"
                    className="btn btn--ghost livestock-batch-dialog__remove"
                    onClick={() => removeLine(line.key)}
                    aria-label={`Remove line ${index + 1}`}
                  >
                    Remove
                  </button>
                )}
              </li>
            );
          })}
        </ul>

        <button type="button" className="btn btn--ghost" onClick={addLine}>
          Add animal
        </button>

        <dl className="livestock-batch-dialog__total">
          <dt>Total</dt>
          <dd>{formatMoney(total)}</dd>
        </dl>

        {formError && (
          <p className="livestock-batch-dialog__form-error" role="alert">
            {formError}
          </p>
        )}

      </form>
    </DialogShell>
  );
}
